import React, { useState, useContext, useEffect } from "react";
import { Box, Typography, Avatar } from "@mui/material";
import { LuArrowBigUp, LuArrowBigDown, LuAward, LuShare2, LuMessageCircle } from "react-icons/lu";
import Utils from "../utils/Utils";
import { AuthContext } from "../context/AuthContext";
import useCommentVote from "../hooks/useCommentVote";
import ShareDialog from "./ShareDialog";
import { useNavigate } from "react-router-dom";

// ── Avatar ────────────────────────────────────────────────────────────────────
function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

function getInitials(username) {
  const parts = username.split("_");
  if (parts.length > 1) return parts.map((p) => p[0].toUpperCase()).join("").slice(0, 2);
  return username.slice(0, 2).toUpperCase();
}

// ── Recursive tree helper — adds a reply under targetId ──────────────────────
function addReplyToTree(comments, targetId, newReply) {
  return comments.map((c) => {
    if (c.commentId === targetId) {
      return { ...c, childComments: [...(c.childComments || []), newReply] };
    }
    if (c.childComments?.length) {
      return { ...c, childComments: addReplyToTree(c.childComments, targetId, newReply) };
    }
    return c;
  });
}

// ── CommentItem ───────────────────────────────────────────────────────────────
function CommentItem({ comment, depth, post, allComments, updateComments }) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [upvoted, setUpvoted] = useState(false);
  const [downvoted, setDownvoted] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const navigate = useNavigate();
  const { isLoggedIn, user } = useContext(AuthContext);
  const { handleVote } = useCommentVote(comment, updateComments);

  const hasReplies = comment.childComments?.length > 0;
  const elapsed = Utils.getFormattedTime(comment.createdAt);

  // Local optimistic vote tally on top of server counts
  const totalVotes =
    (comment.upvotes || 0) - (comment.downvotes || 0) + (upvoted ? 1 : 0) - (downvoted ? 1 : 0);
  const scoreColor = totalVotes > 0 ? "#4ade80" : totalVotes < 0 ? "#f87171" : "#6b7280";

  const handleUpvote = (e) => {
    e.stopPropagation();
    if (downvoted) setDownvoted(false);
    setUpvoted((v) => !v);
    handleVote(comment, "UPVOTE");
  };

  const handleDownvote = (e) => {
    e.stopPropagation();
    if (upvoted) setUpvoted(false);
    setDownvoted((v) => !v);
    handleVote(comment, "DOWNVOTE");
  };

  const handleReplyClick = (e) => {
    e.stopPropagation();
    if (!isLoggedIn) { alert("You must be logged in to reply."); return; }
    setShowReply((v) => !v);
  };

  const handleReplySubmit = async () => {
    if (!replyText.trim() || !isLoggedIn) return;
    setIsSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/comments`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({
            userId: user.id,
            postId: post?.postId || comment.postId,
            content: replyText,
            parentCommentId: comment.commentId,
          }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        const newReply = {
          commentId: data.commentId,
          userName: user.username,
          content: replyText,
          upvotes: 0,
          downvotes: 0,
          createdAt: data.createdAt,
          childComments: [],
        };
        const updated = addReplyToTree(allComments, comment.commentId, newReply);
        updateComments(updated);
        setReplyText("");
        setShowReply(false);
      }
    } catch (err) {
      console.error("Reply failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShare = (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}${window.location.pathname}#comment-${comment.commentId}`;
    setShareUrl(url);
    setShareOpen(true);
  };

  return (
    <Box
      id={`comment-${comment.commentId}`}
      sx={{ pl: depth > 0 ? "4px" : 0, borderLeft: depth > 0 ? "1px solid rgba(55,65,81,0.4)" : "none" }}
    >
      {/* Card */}
      <Box
        onClick={(e) => { e.stopPropagation(); hasReplies && setCollapsed((v) => !v); }}
        sx={{
          backgroundColor: "rgba(26,31,38,0.2)",
          borderRadius: "8px",
          p: "10px",
          cursor: hasReplies ? "pointer" : "default",
          transition: "background-color 0.15s",
          "&:hover": { backgroundColor: "rgba(26,31,38,0.4)" },
        }}
      >
        {/* ── Header ── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "6px" }}>
          {/* Avatar */}
          <Avatar
            sx={{
              bgcolor: stringToColor(comment.userName),
              width: 28,
              height: 28,
              fontSize: "12px",
              fontWeight: 600,
              flexShrink: 0,
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1),0 2px 4px -2px rgba(0,0,0,0.1)",
            }}
          >
            {getInitials(comment.userName)}
          </Avatar>

          {/* Meta */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", lineHeight: 1 }}>
            <Typography
              onClick={(e) => { e.stopPropagation(); navigate(`/profile/${comment.userName}`); }}
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontSize: "14px",
                color: "#4ade80",
                cursor: "pointer",
                lineHeight: 1,
                "&:hover": { color: "#86efac" },
              }}
            >
              {comment.userName}
            </Typography>
            <Typography sx={{ color: "#6b7280", fontSize: "12px", lineHeight: 1 }}>•</Typography>
            <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "12px", color: "#9ca3af", lineHeight: 1 }}>
              {elapsed}
            </Typography>
            {/* Award badge */}
            {comment.awards > 0 && (
              <>
                <Typography sx={{ color: "#6b7280", fontSize: "12px", lineHeight: 1 }}>•</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <LuAward size={11} style={{ color: "#eab308", fill: "currentColor" }} />
                  <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 500, color: "#eab308", lineHeight: 1 }}>
                    {comment.awards}
                  </Typography>
                </Box>
              </>
            )}
            {/* Collapsed hint */}
            {hasReplies && collapsed && (
              <>
                <Typography sx={{ color: "#6b7280", fontSize: "12px", lineHeight: 1 }}>•</Typography>
                <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "12px", color: "#9ca3af", lineHeight: 1 }}>
                  {comment.childComments.length} {comment.childComments.length === 1 ? "reply" : "replies"} hidden
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* ── Body + actions (always visible) ── */}
        <>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "14px",
                color: "#e5e7eb",
                lineHeight: 1.625,
                mb: "8px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {comment.content}
            </Typography>

            {/* ── Action bar ── */}
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              {/* Vote pill */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "rgba(10,14,20,0.5)",
                  border: "1px solid rgba(55,65,83,0.5)",
                  borderRadius: "6px",
                  overflow: "hidden",
                }}
              >
                {/* Up */}
                <Box
                  onClick={handleUpvote}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: "8px",
                    py: "4px",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    backgroundColor: upvoted ? "rgba(34,197,94,0.2)" : "transparent",
                    color: upvoted ? "#4ade80" : "#9ca3af",
                    "&:hover": { backgroundColor: upvoted ? "rgba(34,197,94,0.2)" : "rgba(55,65,83,0.3)", color: "#4ade80" },
                  }}
                >
                  <LuArrowBigUp size={14} style={{ fill: upvoted ? "currentColor" : "none" }} />
                </Box>

                {/* Score */}
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 700,
                    fontSize: "12px",
                    color: scoreColor,
                    px: "6px",
                    minWidth: "24px",
                    textAlign: "center",
                    lineHeight: 1,
                  }}
                >
                  {totalVotes > 0 ? `+${totalVotes}` : totalVotes}
                </Typography>

                {/* Down */}
                <Box
                  onClick={handleDownvote}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: "8px",
                    py: "4px",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    backgroundColor: downvoted ? "rgba(239,68,68,0.2)" : "transparent",
                    color: downvoted ? "#f87171" : "#9ca3af",
                    "&:hover": { backgroundColor: downvoted ? "rgba(239,68,68,0.2)" : "rgba(55,65,83,0.3)", color: "#f87171" },
                  }}
                >
                  <LuArrowBigDown size={14} style={{ fill: downvoted ? "currentColor" : "none" }} />
                </Box>
              </Box>

              {/* Reply */}
              <Box
                onClick={handleReplyClick}
                sx={{
                  display: "flex", alignItems: "center", gap: "4px",
                  px: "10px", py: "4px", borderRadius: "6px",
                  cursor: "pointer", fontSize: "12px", fontWeight: 500,
                  color: "#9ca3af", transition: "all 0.15s",
                  "&:hover": { color: "#4ade80", backgroundColor: "rgba(34,197,94,0.1)" },
                }}
              >
                <LuMessageCircle size={12} />
                <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 500, color: "inherit", lineHeight: 1 }}>
                  Reply
                </Typography>
              </Box>

              {/* Award — commented out until backend is implemented
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "flex", alignItems: "center", gap: "4px",
                  px: "10px", py: "4px", borderRadius: "6px",
                  cursor: "pointer", color: "#9ca3af", transition: "all 0.15s",
                  "&:hover": { color: "#facc15", backgroundColor: "rgba(234,179,8,0.1)" },
                }}
              >
                <LuAward size={12} />
              </Box>
              */}

              {/* Share */}
              <Box
                onClick={handleShare}
                sx={{
                  display: "flex", alignItems: "center", gap: "4px",
                  px: "10px", py: "4px", borderRadius: "6px",
                  cursor: "pointer", color: "#9ca3af", transition: "all 0.15s",
                  "&:hover": { color: "#60a5fa", backgroundColor: "rgba(96,165,250,0.1)" },
                }}
              >
                <LuShare2 size={12} />
              </Box>
            </Box>

            {/* ── Inline reply input ── */}
            {showReply && (
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{ mt: "12px", display: "flex", gap: "10px" }}
              >
                {/* Current user avatar placeholder */}
                <Avatar
                  sx={{
                    bgcolor: user ? stringToColor(user.username || "me") : "#0d737b",
                    width: 24,
                    height: 24,
                    fontSize: "10px",
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {user ? getInitials(user.username || "ME") : "ME"}
                </Avatar>

                <Box sx={{ flex: 1 }}>
                  <textarea
                    placeholder="Write a reply…"
                    rows={2}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleReplySubmit();
                    }}
                    style={{
                      width: "100%",
                      backgroundColor: "#0a0e14",
                      border: "1px solid #374151",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      fontSize: "14px",
                      color: "#e5e7eb",
                      fontFamily: "Inter, sans-serif",
                      resize: "none",
                      outline: "none",
                      boxSizing: "border-box",
                      lineHeight: 1.5,
                    }}
                    onFocus={(e) => { e.target.style.borderColor = "#22c55e"; }}
                    onBlur={(e) => { e.target.style.borderColor = "#374151"; }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "8px" }}>
                    <Box
                      onClick={handleReplySubmit}
                      sx={{
                        px: "12px", py: "4px",
                        background: "linear-gradient(to right, #22c55e, #059669)",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: 600,
                        borderRadius: "6px",
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                        opacity: isSubmitting ? 0.6 : 1,
                        transition: "all 0.15s",
                        fontFamily: "Inter, sans-serif",
                        userSelect: "none",
                        "&:hover": { boxShadow: "0 0 15px rgba(34,197,94,0.2)" },
                      }}
                    >
                      {isSubmitting ? "Posting…" : "Reply"}
                    </Box>
                    <Box
                      onClick={() => { setShowReply(false); setReplyText(""); }}
                      sx={{
                        px: "12px", py: "4px",
                        color: "#9ca3af",
                        fontSize: "12px",
                        fontWeight: 500,
                        cursor: "pointer",
                        fontFamily: "Inter, sans-serif",
                        userSelect: "none",
                        "&:hover": { color: "#d1d5db" },
                      }}
                    >
                      Cancel
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* ── Nested replies (hidden when collapsed) ── */}
            {hasReplies && !collapsed && (
              <Box sx={{ mt: "10px", display: "flex", flexDirection: "column", gap: "10px" }}>
                {comment.childComments.map((reply) => (
                  <CommentItem
                    key={reply.commentId}
                    comment={reply}
                    depth={depth + 1}
                    post={post}
                    allComments={allComments}
                    updateComments={updateComments}
                  />
                ))}
              </Box>
            )}
        </>
      </Box>

      <ShareDialog
        open={shareOpen}
        url={shareUrl}
        handleClose={() => setShareOpen(false)}
        handleCopy={() => { navigator.clipboard.writeText(shareUrl); setShareOpen(false); }}
      />
    </Box>
  );
}

// ── Section container ─────────────────────────────────────────────────────────
const CommentSection = ({ comments, updateComments, post }) => {
  useEffect(() => {
    if (!comments?.length) return;
    const hash = window.location.hash;
    if (hash) {
      const el = document.getElementById(hash.substring(1));
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [comments]);

  if (!comments || comments.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography sx={{ fontFamily: "Inter, sans-serif", color: "#9ca3af", fontSize: "14px", fontStyle: "italic" }}>
          No comments yet — be the first!
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {comments.map((comment) => (
        <CommentItem
          key={comment.commentId}
          comment={comment}
          depth={0}
          post={post}
          allComments={comments}
          updateComments={updateComments}
        />
      ))}
    </Box>
  );
};

export default CommentSection;
